import type { EmailAssistantQueryContext } from "./EmailAssistantQueryContextTypes";

import type { AdminV2CommunicationContactScopeSelection } from "@/types/adminV2/communicationContactScope";
import type { AdminV2EmailAssistantContextWindowResponse } from "@/types/adminV2/messaging";

/** URL IDs are selection only, never address, permissions or reviewed revisions. */
export function parseEmailAssistantContactScopeSelection(
  raw: string | null,
): AdminV2CommunicationContactScopeSelection | null {
  if (!raw || raw.length > 2048) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const uuid = (id: unknown): id is string =>
    typeof id === "string" &&
    /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/iu.test(id);
  if (
    Object.keys(v).length !== 4 ||
    v.version !== "communication_contact_scope_v1" ||
    (v.organisationId !== null && !uuid(v.organisationId)) ||
    (v.relationshipId !== null &&
      (typeof v.relationshipId !== "string" ||
        v.relationshipId.length > 100 ||
        !v.relationshipId.includes(":"))) ||
    (v.organisationId === null && v.relationshipId !== null) ||
    !v.source ||
    typeof v.source !== "object" ||
    Array.isArray(v.source)
  )
    return null;
  const source = v.source as Record<string, unknown>;
  if (
    Object.keys(source).length !== 2 ||
    typeof source.type !== "string" ||
    ![
      "crm_contact",
      "form_submission",
      "onboarding_lead_capture",
      "landing_lead_capture",
      "fhc_report_contact",
    ].includes(source.type) ||
    typeof source.id !== "string" ||
    (source.type === "landing_lead_capture"
      ? !/^[A-Za-z0-9._~-]{8,128}$/u.test(source.id)
      : !uuid(source.id))
  )
    return null;
  return {
    version: "communication_contact_scope_v1",
    organisationId:
      v.organisationId === null ? null : v.organisationId.toLowerCase(),
    source: {
      type: source.type as AdminV2CommunicationContactScopeSelection["source"]["type"],
      id: source.id,
    },
    relationshipId:
      v.relationshipId === null ? null : v.relationshipId.toLowerCase(),
  };
}

/** Compare the complete declared scope tuple, independent of JSON key order. */
export function matchesEmailAssistantContactScopeSelection(
  value: unknown,
  expected: AdminV2CommunicationContactScopeSelection,
): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const received = value as Record<string, unknown>;
  const source = received.source;
  if (!source || typeof source !== "object" || Array.isArray(source))
    return false;
  const receivedSource = source as Record<string, unknown>;
  return (
    Object.keys(received).length === 4 &&
    Object.keys(receivedSource).length === 2 &&
    received.version === expected.version &&
    received.organisationId === expected.organisationId &&
    received.relationshipId === expected.relationshipId &&
    receivedSource.type === expected.source.type &&
    receivedSource.id === expected.source.id
  );
}

/** Both display and readiness require this same current server receipt. */
export function getEmailAssistantContactScopeContext(args: {
  crmHandoff: EmailAssistantQueryContext["crmHandoff"];
  response: AdminV2EmailAssistantContextWindowResponse | undefined;
}) {
  const selection = args.crmHandoff?.contactScope;
  const receipt = args.response?.contactScope;
  const context = receipt?.context;
  const sameSelection =
    selection &&
    matchesEmailAssistantContactScopeSelection(receipt?.selection, selection);
  if (
    !selection ||
    !context ||
    !sameSelection ||
    context.contactId !== args.crmHandoff?.personId ||
    context.organisationId !== selection.organisationId ||
    // The server response remains a runtime boundary despite the client type.
    (context.addressMode as unknown) !== "communication" ||
    !context.scopeRevision ||
    !context.canonicalAddress
  )
    return undefined;
  return context;
}

export function applyEmailAssistantContactScopeContext(args: {
  queryContext: EmailAssistantQueryContext | null;
  response: AdminV2EmailAssistantContextWindowResponse | undefined;
}): EmailAssistantQueryContext | null {
  const query = args.queryContext;
  if (!query?.crmHandoff?.contactScope) return query;
  const context = getEmailAssistantContactScopeContext({
    crmHandoff: query.crmHandoff,
    response: args.response,
  });
  if (!context)
    return {
      ...query,
      recipients: [],
      contextSections: [
        {
          title: "Contact communication context",
          subtitle: "Checking current access and source",
          emptyText:
            "The selected contact context is unavailable. Reopen its owner workflow.",
          items: [],
        },
      ],
    };
  const hold =
    args.response?.sendAvailability ===
    "requested_service_classification_unavailable";
  return {
    ...query,
    crmHandoff: {
      ...query.crmHandoff,
      contactScopeRevision: context.scopeRevision,
    },
    recipients: [
      {
        personId: context.contactId,
        ...(context.organisationId
          ? { organisationId: context.organisationId }
          : {}),
        name: query.recipients[0]?.name ?? "Selected contact",
        email: context.canonicalAddress,
        communicationEmail: context.canonicalAddress,
        sourceLabel: "Checked contact source",
      },
    ],
    contextSections: [
      {
        title: "Contact communication context",
        subtitle: "Checked by the server",
        emptyText: "No additional context.",
        items: [
          {
            label: "Organisation",
            detail: context.organisationId ?? "No organisation",
          },
          { label: "Source", detail: context.source.type.replace(/_/gu, " ") },
          {
            label: "Draft saving",
            detail:
              "Save a personal draft to keep this source and organisation context. Sending needs a fresh review.",
          },
          ...(context.source.capturedAt
            ? [{ label: "Received", detail: context.source.capturedAt }]
            : []),
          ...(hold
            ? [
                {
                  label: "Sending unavailable",
                  detail:
                    "This requested-service reply has no approved content classification. Sending is unavailable.",
                },
              ]
            : []),
        ],
      },
      {
        title: "Recent",
        subtitle: "Last 48 hours in this organisation context",
        emptyText: "No recent email in this context.",
        items: (args.response?.recentCommunications ?? []).map((item) => ({
          label: item.subject ?? "Email",
          detail: `${item.status} · ${item.createdAt}`,
          path: `/email-comms/activity?email=${encodeURIComponent(item.recipientId)}`,
        })),
      },
    ],
  };
}
